import { seatsFromTemplateNodes } from 'components/IncidentConfig/CreateIncident/draftRosterModel';
import type { DraftRosterSeat } from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';

export function seatsFromNodes(
  nodes: RosterTemplateNodeDto[]
): DraftRosterSeat[] {
  return seatsFromTemplateNodes(
    nodes.map((node) => ({
      name: node.name,
      parentName: node.parentName,
      isWorkgroup: node.isWorkgroup,
      category: node.category,
      customCategory: node.customCategory,
    }))
  );
}

/** Seats back to the parentName-keyed shape the apply engine consumes. */
export function nodesFromSeats(
  seats: DraftRosterSeat[]
): RosterTemplateNodeDto[] {
  const nameById = new Map(seats.map((seat) => [seat.id, seat.name]));
  return seats.map((seat) => ({
    name: seat.name.trim(),
    parentName: seat.parentId ? (nameById.get(seat.parentId) ?? null) : null,
    isWorkgroup: !!seat.isWorkgroup,
    category: seat.category,
    customCategory: seat.customCategory,
  }));
}

/**
 * Applying a template wires parents by seat name, so two seats sharing a name
 * would silently collapse into one. Surface that before it is saved rather than
 * letting the server drop a seat.
 */
export type TemplateEditorSeed = {
  name: string;
  description: string | null;
  attachOnly: boolean;
  nodes: RosterTemplateNodeDto[];
};

/**
 * Clone any catalog or library template into a new draft. Nodes are copied so
 * editing the clone cannot mutate the source list the catalog query holds.
 */
export function cloneTemplateAsDraft(source: {
  name: string;
  description: string | null;
  attachOnly: boolean;
  nodes: RosterTemplateNodeDto[];
}): TemplateEditorSeed {
  return {
    name: `Copy of ${source.name}`,
    description: source.description,
    attachOnly: source.attachOnly,
    nodes: source.nodes.map((node) => ({ ...node })),
  };
}

export function duplicateSeatNames(seats: DraftRosterSeat[]): string[] {
  const counts = new Map<string, number>();
  for (const seat of seats) {
    const name = seat.name.trim();
    if (!name) continue;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([name]) => name);
}
